import { DefaultPrimaryButton, InformationModal } from "@/styles/globalstyles";
import {
  Modal,
  Box,
  Typography,
  Divider,
  Autocomplete,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import CloseButton from "../buttons/CloseButton";
import { useGetPartSKPBySupplier, useGetPartBySupplier } from "@/hooks/supplierHooks";
import { useEffect, useMemo, useState } from "react";
import type { partSKP } from "@/api/supplierService";

interface SavePartModalProps {
  open: boolean;
  handleClose: () => void;
  handleSave: (partData: partSKP) => void;
  supplierCode: string;
}

const SavePartModal = ({
  open,
  handleClose,
  handleSave,
  supplierCode,
}: SavePartModalProps) => {
  const [formData, setFormData] = useState<partSKP>({
    part_id: 0,
    part_number: "",
    part_name: "",
    ac: "",
    rt: "",
  });
  const { data: partSKPData } = useGetPartSKPBySupplier(supplierCode);
  const { data: partData = [] } = useGetPartBySupplier(supplierCode, "v2", undefined);

  // filter partSKPData ที่ part_id ยังไม่ถูกใช้ใน supplier
  const partDropdownOptions = useMemo(() => {
    if (!partSKPData) return [];
    // part_id ที่มีอยู่แล้วใน supplier
    const usedPartIds = new Set(partData.map((p) => p.part_id));
    // filter เฉพาะ part_number ที่ part_id ยังไม่ถูกใช้
    const filteredSKP = partSKPData.filter((part) => !usedPartIds.has(part.part_id));
    const uniquePartNos = Array.from(new Set(filteredSKP.map((part) => part.part_number)));
    return uniquePartNos.map((partNo) => ({
      label: partNo,
      value: partNo,
    }));
  }, [partSKPData, partData]);

  const filteredPartSKP = useMemo(() => {
    if (!partSKPData || !formData.part_number) return [];
    // filter เฉพาะ part ที่ยังไม่ถูกใช้
    const usedPartIds = new Set(partData.map((p) => p.part_id));
    return partSKPData.filter(
      (part) => part.part_number === formData.part_number && !usedPartIds.has(part.part_id)
    );
  }, [formData.part_number, partSKPData, partData]);

  useEffect(() => {
    if (filteredPartSKP.length > 0) {
      setFormData((prev) => ({
        ...prev,
        part_id: filteredPartSKP[0].part_id,
        part_name: filteredPartSKP[0].part_name,
        ac: filteredPartSKP[0].ac,
        rt: filteredPartSKP[0].rt,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        part_id: 0,
        part_name: "",
        ac: "",
        rt: "",
      }));
    }
  }, [filteredPartSKP]);

  const handleCloseModal = () => {
    setFormData({
      part_id: 0,
      part_number: "",
      part_name: "",
      ac: "",
      rt: "",
    });
    handleClose();
  };

  const getPartString = (part: partSKP) => {
    if (!part.ac) return `${part.part_number} - ${part.part_name}`;
    else if (!part.rt)
      return `${part.part_number} - ${part.part_name} - ${part.ac}`;
    else
      return `${part.part_number} - ${part.part_name} - ${part.ac} - ${part.rt}`;
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...InformationModal, maxHeight: "90vh" }}>
        <CloseButton onClick={handleCloseModal} />
        <Typography variant="h2">เพิ่ม Part</Typography>
        <Divider sx={{ my: 1 }} />
        <Box p={1} display="flex" flexDirection="column" gap={2}>
          <Autocomplete
            fullWidth
            value={
              partDropdownOptions.find(
                (opt) => opt.value === formData.part_number
              ) || null
            }
            onChange={(_event, newValue) => {
              setFormData((prev) => ({
                ...prev,
                part_number: newValue ? newValue.value : "",
              }));
            }}
            options={partDropdownOptions}
            renderInput={(params) => (
              <TextField {...params} label="Part Number" />
            )}
          />
          <FormControl disabled={filteredPartSKP.length === 0} fullWidth>
            <InputLabel
              id="part-label"
              shrink={formData.part_id !== 0}
              sx={{
                "&.Mui-focused": {
                  color: "primary.main",
                },
                "&.Mui-disabled": {
                  color: "#999999",
                },
              }}
            >
              Part Number - Part Name - AC - Routing
            </InputLabel>
            <Select
              labelId="part-label"
              value={formData.part_id === 0 ? "" : formData.part_id}
              notched={formData.part_id !== 0}
              onChange={(event) => {
                setFormData((prev) => ({
                  ...prev,
                  part_id:
                    event.target.value === null
                      ? 0
                      : Number(event.target.value),
                  part_name:
                    filteredPartSKP.find(
                      (part) => part.part_id === Number(event.target.value)
                    )?.part_name || "",
                  ac:
                    filteredPartSKP.find(
                      (part) => part.part_id === Number(event.target.value)
                    )?.ac || "",
                  rt:
                    filteredPartSKP.find(
                      (part) => part.part_id === Number(event.target.value)
                    )?.rt || "",
                }));
              }}
              label="Part Number - Part Name - AC - Routing"
            >
              {filteredPartSKP?.map((part) => (
                <MenuItem key={part.part_id} value={part.part_id}>
                  {getPartString(part)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            sx={DefaultPrimaryButton}
            onClick={() => {
              handleSave(formData);
              handleCloseModal();
            }}
          >
            บันทึก
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SavePartModal;
